import { createSlice, createEntityAdapter, createAsyncThunk } from '@reduxjs/toolkit';
import { addSeconds } from 'date-fns';

import { s3Service } from '~/services';
import { LOADING_STATUSES } from '~/constants/edf';
import { saveBlobAsFile } from '~/utils/blob';
import EdfWriter from '~/services/Edf/edf-annotation-writer';
import { serialize } from '~/services/Edf/test-results-serializer';
import intervalTreeService from '~/services/intervalTree';
import {
    annotationMetaSelector,
    getSleepResultsSelector,
    hrSelector,
    recLengthSelector,
    spo2Selector,
    startDateSelector,
} from '~/selectors/edf';

import { initEdf, reset } from './loadingFiles';

function initAnnotations(state, annotations) {
    for (let i = 0; i < annotations.length; i++) {
        annotations[i].id = i;
    }

    adapter.upsertMany(state, annotations);

    // hack to reuse adapter's selector. it returns sorted array of annotations
    intervalTreeService.seedTree(adapter.getSelectors((state) => state).selectAll(state));
}

export const adapter = createEntityAdapter({
    sortComparer: (a, b) => a.onset - b.onset,
});

const initialState = adapter.getInitialState({
    meta: {
        testId: null,
        revisionId: null,
        trackingNumber: null,
    },
    updateAnnotationStatus: null,
});
Object.freeze(initialState);

const annotationsSlice = createSlice({
    name: 'edfAnnotations',
    initialState,
    reducers: {
        /**
         *
         * @param {Object} payload
         * @param {Number|null} payload.id
         * @param {Number} payload.onset
         * @param {Number} payload.duration
         */
        update: (state, { payload }) => {
            if (payload.id != null) {
                intervalTreeService.updateIntervalTree(state.entities[payload.id], payload);
            } else {
                const currentId = Math.max(...state.ids);
                payload.id = Number.isFinite(currentId) ? currentId + 1 : 0; // set next ID

                intervalTreeService.addToIntervalTree(payload);
            }

            adapter.upsertOne(state, payload);
        },
        remove: (state, { payload: { id } }) => {
            intervalTreeService.removeFromIntervalTree(state.entities[id]);

            adapter.removeOne(state, id);
        },
        resetUpdateAnnotationStatus: (state) => {
            state.updateAnnotationStatus = initialState.updateAnnotationStatus;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initEdf, (state, { payload: { annotations, meta = {} } }) => {
                initAnnotations(state, annotations);
                state.meta.trackingNumber = meta.trackingNumber;
                state.meta.revisionId = meta.revisionId;
                state.meta.testId = meta.testId;
            })
            .addCase(reset, () => {
                intervalTreeService.reset();

                return initialState;
            })
            .addCase(saveAnnotations.fulfilled, (state) => {
                state.updateAnnotationStatus = LOADING_STATUSES.SUCCESS;
            })
            .addCase(saveAnnotations.pending, (state) => {
                state.updateAnnotationStatus = LOADING_STATUSES.PENDING;
            })
            .addCase(saveAnnotations.rejected, (state) => {
                state.updateAnnotationStatus = LOADING_STATUSES.ERROR;
            });
    },
});

const getEdfResults = (state) => {
    const recLength = recLengthSelector(state);
    const recStart = startDateSelector(state);
    const { min: minHr, max: maxHr, avg: avgHr } = hrSelector(state);
    const { min: minSpo2, max: maxSpo2, avg: avgSpo2 } = spo2Selector(state);

    return serialize({
        ...getSleepResultsSelector(state),
        minSpo2,
        maxSpo2,
        avgSpo2,
        minHr,
        maxHr,
        avgHr,
        recordingStart: recStart,
        recordingEnd: addSeconds(new Date(recStart), recLength).toISOString(),
        recordingTime: recLength,
    });
};
export const exportAnnotations = createAsyncThunk(
    'annotations/exportAnnotations',
    async (_, thunkAPI) => {
        await saveBlobAsFile(
            EdfWriter.createFile(
                adapter
                    .getSelectors((state) => state.app.edf.info.annotations)
                    .selectAll(thunkAPI.getState()),
                'annotations'
            )
        );
    }
);
export const saveAnnotations = createAsyncThunk(
    'annotations/saveAnnotations',
    async ({ comment, results }, thunkAPI) => {
        const state = thunkAPI.getState();
        const serializedResults = getEdfResults(state);
        const { testId, revisionId } = annotationMetaSelector(state);

        const pendingRevision = await s3Service.submitTestResults(
            testId,
            revisionId,
            comment,
            serializedResults
        );

        const { data: s3URLs } = await s3Service.getRevisionSignedURLS(
            pendingRevision.data.data.id
        );

        await s3Service.uploadBlob(
            s3URLs.annotations,
            EdfWriter.createFile(
                adapter.getSelectors((state) => state.app.edf.info.annotations).selectAll(state),
                `${pendingRevision.data.id}.annotations`
            )
        );
    }
);

export const { update, remove, resetUpdateAnnotationStatus } = annotationsSlice.actions;
export default annotationsSlice.reducer;
